import {
  DevicePresenceStateDto,
  IotDeviceStateOfUse,
  QaEquipmentCatalogDto,
  QaEquipmentRtSystemDto,
} from '@/api/generator';
import { useDeviceStatusSignalR } from '@/components/deviceStatus/utils/useDeviceStatusSignalR';
import { connectedArrayInfoOf } from '@/components/deviceStatus/utils/deviceInformation';
import { isIOTDevice } from '@/utils/rtSystem';
import { computed, MaybeRefOrGetter, toValue } from 'vue';

/**
 * Why a zero measurement cannot be started on a detector interface right now:
 * the interface is busy with a measurement (its own, or one another client
 * started, or one whose stop failed), it is not online (offline, in error, or
 * no presence message has arrived yet), or it is online but reports no
 * connected array.
 */
export type ZeroMeasurementBlocker = 'busy' | 'offline' | 'noArray';

type Interface = QaEquipmentRtSystemDto | QaEquipmentCatalogDto;

/**
 * Only an IoT interface reports its presence, so only it can be gated. A
 * deregistered one simply never reports, so it reads as offline. Other
 * equipment is left to the backend's own validation when the run starts.
 */
function reportsPresence(iface: Interface): boolean {
  return isIOTDevice(iface);
}

/**
 * The blocker for starting a zero measurement on the interface, or undefined
 * when it may start. Pure, so the rule is testable without the hub.
 */
export function zeroMeasurementBlockerOf(
  iface: Interface | undefined,
  presence: DevicePresenceStateDto | undefined,
): ZeroMeasurementBlocker | undefined {
  if (!iface || !reportsPresence(iface)) {
    return undefined;
  }
  if (
    presence?.state === IotDeviceStateOfUse.Initializing ||
    presence?.state === IotDeviceStateOfUse.Measuring
  ) {
    return 'busy';
  }
  if (presence?.state !== IotDeviceStateOfUse.Online) {
    return 'offline';
  }
  return connectedArrayInfoOf(presence.deviceInformation) ? undefined : 'noArray';
}

/**
 * Live blocker for the given interface, following its presence messages.
 */
export function useZeroMeasurementBlocker(iface: MaybeRefOrGetter<Interface | undefined>) {
  const presence = useDeviceStatusSignalR(() => toValue(iface)?.id);

  return computed(() => zeroMeasurementBlockerOf(toValue(iface), presence.value));
}
